/**
 * skipLink.ts — слово владельца 21.10.2026 (задание stylepack/skip-link):
 * ссылка «Пропустить» под кнопкой «Далее». Здесь — зеркало границ сервера,
 * подпись ссылки и её стиль для публичной отрисовки (SkipLinkButton.tsx).
 *
 * Границы — ЗЕРКАЛО канона сервера `SKIP_LINK_BOUNDS`
 * (backend/src/ai-funnels/block-schema/skip-link.ts); разъезд ловит сторож
 * `skip-link.look-ends.spec.ts` — он читает ЭТОТ объект с диска, форму
 * `{ min: N, max: N }` не менять, не обновив его.
 *
 * Нет подписи — ссылки нет (разметка кнопки прежняя).
 *
 * `skipOnly` (доработка 21.10.2026): только ссылка, без главной кнопки —
 * экраны учебного набора с автопереходом и одной ссылкой «Skip». Действует
 * лишь при непустой подписи (`skipOnlyActive`): пустой экран без выхода
 * недопустим. Ссылка без кнопки стоит в потоке — прибивку к низу пака
 * снимает `dockUnlessSkipOnly`.
 */
import type { CSSProperties } from 'react';
import { clampLook } from './textLook';
import { trimmedColor } from './semanticTextRoles';

/** Типы со ссылкой — зеркало `SKIP_LINK_BLOCK_TYPES` сервера. */
export const SKIP_LINK_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'button_next',
]);

/** Длина подписи — зеркало `SKIP_TEXT_MAX_LENGTH` сервера. */
export const SKIP_TEXT_MAX_LENGTH = 40;

export const SKIP_LINK_BOUNDS = {
  skipFontSize: { min: 10, max: 24 },
  skipLetterSpacing: { min: 0, max: 4 },
  skipFontWeight: { min: 400, max: 800 },
} as const;

/** Кегль ссылки, пока поле пусто. */
export const SKIP_FONT_SIZE_DEFAULT = 15;

/** Цвет ссылки, пока поле пусто, — вторичный цвет текста темы. */
const SKIP_COLOR_DEFAULT = 'var(--color-text-secondary, #8c8c8c)';

export interface SkipLinkFields {
  skipText?: string;
  skipColor?: string;
  skipFontSize?: number;
  skipTextTransform?: 'none' | 'uppercase';
  skipLetterSpacing?: number;
  skipFontWeight?: number;
  skipOnly?: boolean;
}

/** Подпись ссылки без крайних пробелов; пусто — ссылки нет (`null`). */
export function skipLinkLabel(fields: SkipLinkFields): string | null {
  if (typeof fields.skipText !== 'string') return null;
  const label = fields.skipText.trim();
  return label === '' ? null : label;
}

/** Только ссылка, без кнопки: флажок включён И подпись непустая. */
export function skipOnlyActive(fields: SkipLinkFields): boolean {
  return fields.skipOnly === true && skipLinkLabel(fields) !== null;
}

/**
 * Прибивка кнопки к низу (пак 'fixed-bottom'): у ссылки без кнопки её нет —
 * ссылка стоит в потоке, где стоял бы блок, без подложки и запаса снизу.
 */
export function dockUnlessSkipOnly<T>(
  fields: SkipLinkFields,
  dock: T | null,
): T | null {
  return skipOnlyActive(fields) ? null : dock;
}

/**
 * Стиль ссылки: без рамки и заливки, отступ сверху 16, шрифт кнопки
 * (`font: inherit` — у `<button>` браузер ставит свой). Поля автора —
 * в границах сервера; жирность сотнями.
 */
export function skipLinkStyle(fields: SkipLinkFields): CSSProperties {
  const b = SKIP_LINK_BOUNDS;
  const out: CSSProperties = {
    display: 'inline-block',
    marginTop: 16,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    fontSize:
      clampLook(fields.skipFontSize, b.skipFontSize) ?? SKIP_FONT_SIZE_DEFAULT,
    color: trimmedColor(fields.skipColor) ?? SKIP_COLOR_DEFAULT,
  };
  if (
    fields.skipTextTransform === 'none' ||
    fields.skipTextTransform === 'uppercase'
  ) {
    out.textTransform = fields.skipTextTransform;
  }
  const spacing = clampLook(fields.skipLetterSpacing, b.skipLetterSpacing);
  if (spacing !== undefined) out.letterSpacing = `${spacing}px`;
  const weight = clampLook(fields.skipFontWeight, b.skipFontWeight);
  if (weight !== undefined) out.fontWeight = Math.round(weight / 100) * 100;
  return out;
}
